import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "../../lib/api";
import { safePath } from "../../lib/nav";
import { toast } from "../../lib/notify";
import { Plus, Search } from "lucide-react";
import { ConfirmDelete } from "../../components/ConfirmDelete";
import { useColFilters } from "../../components/ColFilters";
import { useAuth } from "../../context/AuthContext";
import CategoryForm from "../../components/services/CategoryForm";
import ServiceForm from "../../components/services/ServiceForm";
import ServiceTable from "../../components/services/ServiceTable";
import CategoryGroup, { OrphanGroup } from "../../components/services/CategoryGroup";
import { SVC_COLS, emptySvc, emptyCat, svcPayload } from "../../components/services/servicesShared";

const norm = (s) => (s || "").toLocaleLowerCase("tr");

// tanım verileri + odaklanınca tazeleme
function useServiceData() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [svcTotals, setSvcTotals] = useState({});
  const refetch = useCallback(() => {
    http.get("/categories").then((r) => setCategories(r.data));
    http.get("/services").then((r) => setServices(r.data));
    http.get("/projects").then((r) => setProjects(r.data));
    http.get("/locations").then((r) => setLocations(r.data));
    http.get("/services/totals").then((r) => setSvcTotals(r.data));
  }, []);
  useEffect(() => { refetch(); }, [refetch]);
  // başka sekmede/sayfada tanım değişirse odaklanınca listeleri tazele
  useEffect(() => {
    window.addEventListener("focus", refetch);
    return () => window.removeEventListener("focus", refetch);
  }, [refetch]);
  return { categories, services, projects, locations, svcTotals, refetch };
}

// uyarı bağlantısından gelindiyse (?don=) ESC ile aynı ekrana / aynı filtrelerle geri dön (SPA içi — tam sayfa yenileme yok)
function useEscapeBack() {
  const navigate = useNavigate();
  useEffect(() => {
    const don = safePath(new URLSearchParams(window.location.search).get("don"));
    if (!don) return;
    const onKey = (e) => { if (e.key === "Escape" && !e.target.closest("input,textarea,select")) { e.stopPropagation(); navigate(don); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [navigate]);
}

export default function ServicesPage() {
  const { isAdmin } = useAuth();
  const { categories, services, projects, locations, svcTotals, refetch } = useServiceData();
  useEscapeBack();
  const [delCat, setDelCat] = useState(null); // üç aşamalı silme onayı bekleyen başlık
  const [search, setSearch] = useState("");
  const [projFilter, setProjFilter] = useState("");   // proje kapsam filtresi: yalnız o projede görünen başlık/hizmetler
  const scopeVisible = (x, pid) => !pid || (x.scope !== "projects" || (x.project_ids || []).includes(pid));
  const visibleCats = useMemo(() => categories.filter((c) => scopeVisible(c, projFilter)), [categories, projFilter]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [catEditing, setCatEditing] = useState(null);
  const [catForm, setCatForm] = useState(emptyCat());
  const [svcEditing, setSvcEditing] = useState(null);
  const [svcForm, setSvcForm] = useState(emptySvc);
  const [svcCatLocked, setSvcCatLocked] = useState(false);

  // arama: hizmet/başlık adı; kalem eşleşirse üst hizmeti, üst hizmet eşleşirse kalemleri de görünür; proje filtresi: kapsamı o projeyi içerenler (projeye özel hizmetler dahil)
  const visible = useMemo(() => {
    const inProj = (s) => !projFilter || ((!s.project_id || s.project_id === projFilter) && scopeVisible(s, projFilter) && scopeVisible(categories.find((c) => c.id === s.category_id) || {}, projFilter));
    const match = (s) => !!s && inProj(s) && (!search.trim() || norm(s.name).includes(norm(search)) || norm(categories.find((c) => c.id === s.category_id)?.name).includes(norm(search)));
    return services.filter((s) => inProj(s) && (match(s) || services.some((k) => k.parent_id === s.id && match(k)) || (s.parent_id && match(services.find((p) => p.id === s.parent_id)))));
  }, [services, categories, search, projFilter]);   // eslint-disable-line react-hooks/exhaustive-deps
  const svcByCat = (cid) => visible.filter((s) => s.category_id === cid);
  const orphans = useMemo(() => visible.filter((s) => !categories.some((c) => c.id === s.category_id)), [visible, categories]);

  const submitCat = async (e) => {
    e.preventDefault();
    try {
      if (catEditing === "new") await http.post("/categories", catForm);
      else await http.put(`/categories/${catEditing}`, catForm);
      toast.success("Hizmet başlığı kaydedildi");
      setCatEditing(null); setCatForm(emptyCat()); refetch();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeCat = async () => {
    if (!delCat) return;
    try {
      await http.delete(`/categories/${delCat.id}`);
      toast.success(`"${delCat.name}" ve altındaki hizmetler silindi — Silinenler menüsünden geri alabilirsiniz`);
      setDelCat(null); refetch();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const closeSvc = () => { setSvcEditing(null); setSvcForm(emptySvc); setSvcCatLocked(false); };
  const submitSvc = async (e) => {
    e.preventDefault();
    try {
      const payload = svcPayload(svcForm);
      if (svcEditing === "new") await http.post("/services", payload);
      else await http.put(`/services/${svcEditing}`, payload);
      toast.success(payload.parent_id ? "Kalem kaydedildi — üst hizmetin görüldüğü tüm projelerde fiyatlanabilir" : payload.scope === "projects" ? `Hizmet kaydedildi — yalnız seçili ${payload.project_ids.length} projede kullanılabilir` : "Hizmet kaydedildi — tüm projelerde kullanılabilir");
      closeSvc(); refetch();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeSvc = async (id) => {
    const n = services.filter((k) => k.parent_id === id).length;
    if (!window.confirm(n ? `Hizmet ve altındaki ${n} kalem silinsin mi?` : "Hizmet silinsin mi?")) return;
    try { await http.delete(`/services/${id}`); toast.success("Silindi"); refetch(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const editSvc = (s) => { setSvcForm({ ...emptySvc, ...s, location_ids: s.location_ids || [], project_ids: s.scope === "projects" ? (s.project_ids || []) : [], billing_basis: s.billing_basis || "" }); setSvcCatLocked(!!s.parent_id); setSvcEditing(s.id); };
  const addSvcTo = (c) => { setSvcForm({ ...emptySvc, category_id: c.id }); setSvcCatLocked(true); setSvcEditing("new"); };
  // kalem: üst hizmetin altında açılır (başlık ve kapsam üstten), birimi üst hizmetten başlar
  const addKalemTo = (p) => { setSvcForm({ ...emptySvc, category_id: p.category_id, parent_id: p.id, unit: p.unit || "kişi" }); setSvcCatLocked(true); setSvcEditing("new"); };
  const svcParent = svcForm.parent_id ? services.find((s) => s.id === svcForm.parent_id) || null : null;
  const editCat = (c) => { setCatForm({ name: c.name, status: c.status, scope: c.scope || "global", project_ids: c.project_ids || [] }); setCatEditing(c.id); };

  const locGroups = useMemo(() => projects
    .map((pr) => ({ pr, locs: locations.filter((l) => l.project_id === pr.id && !l.is_correction && l.branch_type !== "DÜZELTME") }))   // düzeltme şubeleri seçilemez
    .filter((g) => g.locs.length > 0), [projects, locations]);

  const svcFin = useCallback((id) => {
    const t = svcTotals[id] || { revenue: 0, expense: 0 };
    return { ...t, diff: Math.round((t.revenue - t.expense) * 100) / 100 };
  }, [svcTotals]);

  const [shf, setShf, svcVisRows, sAutoOpts] = useColFilters(services, SVC_COLS);
  const visibleIds = useMemo(() => new Set(svcVisRows.map((s) => s.id)), [svcVisRows]);
  const table = (rows) => <ServiceTable rows={rows} visibleIds={visibleIds} hf={shf} setHf={setShf} autoOpts={sAutoOpts} svcFin={svcFin} onEdit={editSvc} onRemove={removeSvc} onAddKalem={addKalemTo} />;

  return (
    <div className="space-y-6" data-testid="page-def-services">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Hizmetler</h1>
          <p className="text-sm text-slate-500 mt-1">Hizmet başlıkları grup başlığıdır; hizmetler altında listelenir, bir hizmetin altına <b>kalemler</b> (örn. ASIL PERSONEL HAKEDİŞİ → Temizlik Görevlisi, Şef) açılabilir ve her birine Kişi Bazlı / Aylık hesap esası verilir. Başlıklar/hizmetler varsayılan olarak tüm projelerde görünür — proje kartından belirli projelere özel hale getirilebilir veya projeye özel yenileri tanımlanabilir.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={projFilter} onChange={(e) => setProjFilter(e.target.value)} data-testid="def-services-project-filter" title="Yalnız seçili projede görünen başlık ve hizmetleri listele"
            className={`px-2 py-2 text-xs border rounded-md bg-white w-56 focus:border-amber-500 outline-none ${projFilter ? "border-amber-500 text-amber-900 font-semibold" : "border-slate-300"}`}>
            <option value="">Tüm projeler (kapsam filtresi yok)</option>
            {[...projects].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {projFilter && <span className="text-[11px] font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="def-services-project-filter-count">{visible.filter((s) => !s.parent_id).length} hizmet · {visible.filter((s) => s.parent_id).length} kalem bu projede</span>}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hizmet veya başlık ara…"
              data-testid="def-services-search"
              className="pl-8 pr-3 py-2 text-xs border border-slate-300 rounded-md bg-white w-52 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            />
          </div>
          {!catEditing && (
            <button onClick={() => { setCatForm(emptyCat()); setCatEditing("new"); }} data-testid="cat-add"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
              <Plus className="w-4 h-4" /> Başlık Ekle
            </button>
          )}
        </div>
      </div>

      {catEditing && <CategoryForm form={catForm} setForm={setCatForm} onSubmit={submitCat} onCancel={() => setCatEditing(null)} />}

      {svcEditing && <ServiceForm form={svcForm} setForm={setSvcForm} catLocked={svcCatLocked} categories={categories} locations={locations} locGroups={locGroups} projects={projects} parent={svcParent} onSubmit={submitSvc} onCancel={closeSvc} />}

      <div className="space-y-3">
        {visibleCats.map((c) => {
          const cSvcs = svcByCat(c.id);
          if (projFilter && cSvcs.length === 0) return null;   // proje filtresinde hizmetsiz başlıklar gizlenir
          return (
            <CategoryGroup key={c.id} c={c} count={cSvcs.filter((s) => !s.parent_id).length} kalemCount={cSvcs.filter((s) => s.parent_id).length} isAdmin={isAdmin} onAddSvc={addSvcTo} onEdit={editCat} onDelete={setDelCat}>
              {table(cSvcs)}
            </CategoryGroup>
          );
        })}
        {orphans.length > 0 && <OrphanGroup count={orphans.length}>{table(orphans)}</OrphanGroup>}
        {projFilter && visible.length === 0 && (
          <div className="text-xs text-slate-500 py-3 bg-amber-50 border border-amber-200 rounded-md px-3" data-testid="services-project-empty">Bu projede görünen hizmet yok — hizmet formundaki proje seçiminden bu projeye atayabilirsiniz.</div>
        )}
        {categories.length === 0 && (
          <div className="text-xs text-slate-500 py-3 bg-amber-50 border border-amber-200 rounded-md px-3" data-testid="services-empty-hint">
            Henüz hizmet başlığı yok. Hizmet ekleyebilmek için önce <span className="font-semibold">"Başlık Ekle"</span> ile başlık oluşturun.
          </div>
        )}
      </div>

      {delCat && (
        <ConfirmDelete testid="cat-del" title={`Hizmet Başlığı Sil: ${delCat.name}`}
          message={`"${delCat.name}" başlığı ve altındaki ${services.filter((s) => s.category_id === delCat.id).length} hizmet birlikte silinecek — veriler çöp kutusuna taşınır. Emin misiniz?`}
          step2Message={`Son onay: "${delCat.name}" ve altındaki tüm hizmetler silinsin mi?`}
          onConfirm={removeCat} onCancel={() => setDelCat(null)} />
      )}
    </div>
  );
}
